import type { ModuleSnapshot } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { ApiError } from '../errors.ts'
import { keys } from '../keys.ts'
import { normalizeList } from '../page.ts'

/**
 * 모듈 **실행 취소 중계**(`IFC-RO-50`) — `POST /modules/{id}/control/executions/{execution_id}/cancel`.
 *
 * 🔴 **왜 필요한가**(eric 2026-09-28 「작업 중지하는 api 가 없어? 끝까지 돌려야 하는 거야?」 → 「프론트에서 처리」).
 *    `POST /batches/{id}/stop` · `recover(discard)` 는 **작업 상태만** `aborted` 로 바꾼다 — 모듈에는
 *    신호가 안 간다(`ctcells-core` `services/batches.stop` = 상태 전이 + `handoff_run.settle_on_stop` 뿐).
 *    모듈은 받은 블록을 끝까지 돈다. 그래서 중지가 받아들여진 **뒤에** 그 작업의 실행을 쥔 모듈에
 *    이 취소를 이어 보낸다(`docs/backend-requests.csv` BR-97).
 *
 * 실물(`openapi.json` · `app/views/module_control.py` `cancel_module_execution_control` · 2026-09-28)
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 본문 | `CancelModuleExecutionControlBody{command_id(필수), reason?}` · **본문 자체는 선택**(`Body | None`) |
 * | 헤더 | `X-Control-Token` — `require_control_lock` 의존(`controlToken.ts` 표에 등재) |
 * | 역할 | `mc.WRITE_ROLES` |
 * | 성공 | **202** `{module_id, command_id}` (openapi 는 200 · 빈 스키마 — 실물이 202 다) |
 * | 거절 | 409 `MODULE_LOCAL_MODE` · 503(모듈에 못 닿음) · 모듈 거절은 그 코드 그대로 |
 *
 * 🔴 **`command_id` 를 늘 싣는다.** 중계가 모듈에 보내는 본문은 언제나 `{command_id, …}` 다
 *    (`_relay` 가 없으면 지어 싣는다) — 모듈 로컬에 `{}` 를 보내면 500 이었던 것과는 다른 길이다.
 *    우리가 짓는 까닭은 **멱등**이다: 재시도에 같은 값을 다시 넘기면 서버가 첫 결과를 돌려준다.
 * ⚠ **`MODULE_BUSY(batch_running)` 거절은 v0.9.1 에서 걷혔다**(core `75fca33` — 배치 중에도 장비 제어를 연다).
 */

/** 중계 응답(202). 결과는 모듈 상태 구독으로 온다 — 낙관적으로 쓸 값이 없다 */
export interface ModuleExecutionCancelAccepted {
  module_id?: string
  command_id?: string
}

export interface CancelModuleExecutionVars {
  moduleId: string
  executionId: string
  /** 모듈 `ExecutionStatus.terminal_reason` 에 그대로 실린다. 비었으면 키를 싣지 않는다 */
  reason?: string
  /** 재시도 시 **같은 값을 다시 넘긴다** */
  commandId?: string
}

/** 요청 한 건 — 훅과 묶음 취소가 같이 쓴다(본문 모양의 정의처는 여기 하나다) */
export function cancelModuleExecution({
  moduleId,
  executionId,
  reason,
  commandId,
}: CancelModuleExecutionVars): Promise<ModuleExecutionCancelAccepted> {
  return orchestrator.request<ModuleExecutionCancelAccepted>({
    method: 'POST',
    path: `/modules/${encodeURIComponent(moduleId)}/control/executions/${encodeURIComponent(executionId)}/cancel`,
    body: {
      command_id: commandId ?? newCommandId(),
      ...(reason === undefined || reason.trim() === '' ? {} : { reason }),
    },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

export const useCancelModuleExecution = createCommand<
  CancelModuleExecutionVars,
  ModuleExecutionCancelAccepted
>({
  send: cancelModuleExecution,
  invalidates: () => [keys.modules.all()],
})

/** 모듈 한 대가 쥔 실행 — `ModuleSnapshot.current_execution` 에서 온 값 그대로 */
export interface ModuleExecutionRef {
  moduleId: string
  /** 종류 원값(`centrifuge` …). 한글 표기는 표시 계층의 몫이다 */
  moduleType?: string
  executionId: string
  batchId: string
}

function refOf(module: ModuleSnapshot): ModuleExecutionRef | undefined {
  const exec = module.current_execution
  if (exec == null) return undefined
  const executionId = exec.execution_id
  const batchId = exec.batch_id
  // 🔴 서버가 귀속을 안 실었으면(손으로 돌린 실행 등) **작업의 것이 아니다** — 짐작해 잇지 않는다
  if (typeof executionId !== 'string' || executionId === '') return undefined
  if (typeof batchId !== 'string' || batchId === '') return undefined
  return {
    moduleId: module.module_id,
    ...(typeof module.module_type === 'string' ? { moduleType: module.module_type } : {}),
    executionId,
    batchId,
  }
}

/**
 * **그 작업의 실행을 지금 쥔 모듈 전부.** 판정은 서버가 준 `current_execution.batch_id` 하나다.
 * 🔴 다른 작업의 실행 · 귀속 없는 실행은 건드리지 않는다.
 */
export function executionsOfBatch(
  modules: readonly ModuleSnapshot[],
  batchId: string,
): ModuleExecutionRef[] {
  return modules.flatMap((module) => {
    const ref = refOf(module)
    return ref !== undefined && ref.batchId === batchId ? [ref] : []
  })
}

/**
 * **끝난 작업의 실행이 장비에 남아 있는가** — 실행이 가리키는 작업이 «진행 중 목록»에 없으면 남은 것이다.
 *
 * @param activeBatchIds `GET /batches?phase=active` 가 준 작업 id 들(종착이 아닌 것). 🔴 서버가 준 목록이다 —
 *   화면이 상태 이름을 보고 종착을 가르지 않는다.
 * ⚠ 결과는 **알림 재료**다. 자동으로 취소를 쏘지 않는다 — 사람이 누른다.
 */
export function leftoverExecutions(
  modules: readonly ModuleSnapshot[],
  activeBatchIds: ReadonlySet<string>,
): ModuleExecutionRef[] {
  return modules.flatMap((module) => {
    const ref = refOf(module)
    return ref !== undefined && !activeBatchIds.has(ref.batchId) ? [ref] : []
  })
}

/** 한 대의 취소가 실패한 기록 — 화면이 「<모듈> <사유>(<코드>)」로 적는다 */
export interface CancelFailure {
  ref: ModuleExecutionRef
  error: ApiError
}

export interface CancelBatchExecutionsResult {
  batchId: string
  /** 중계가 받아들인 것(202). ⚠ 「멈췄다」가 아니다 — 멈춤은 모듈 상태 구독이 말한다 */
  accepted: readonly ModuleExecutionRef[]
  failures: readonly CancelFailure[]
}

export interface CancelBatchExecutionsVars {
  batchId: string
  reason?: string
}

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  return new ApiError({
    status: 0,
    message: error instanceof Error ? error.message : String(error),
  })
}

/**
 * **그 작업의 실행을 쥔 모듈 전부에 취소를 보낸다.**
 *
 * 1. `GET /modules` **한 번**(보내는 순간의 사실 · 주기 조회 아님) → `executionsOfBatch`
 * 2. 대상마다 취소 중계 — 🔴 **한 대의 실패가 나머지를 막지 않는다.** 실패는 모아서 돌려준다
 *
 * 🔴 **중지가 받아들여진 «뒤»에만 부른다**(부르는 쪽 책임). 작업 상태는 이 결과로 되돌리지 않는다 —
 *    중지는 이미 서버에서 종착이다.
 * ⚠ 대상이 0건이면 아무 요청도 안 나간다(모듈이 이미 블록을 끝냈다) — 그것도 성공이다.
 * ⚠ `GET /modules` 가 실패하면 **이 명령이 실패한다**(대상을 모른다 ≠ 대상이 없다).
 */
export async function cancelBatchExecutions({
  batchId,
  reason,
}: CancelBatchExecutionsVars): Promise<CancelBatchExecutionsResult> {
  const listed = normalizeList<ModuleSnapshot>(
    await orchestrator.request({ method: 'GET', path: '/modules' }),
    {},
  )
  const targets = executionsOfBatch(listed.items, batchId)
  const accepted: ModuleExecutionRef[] = []
  const failures: CancelFailure[] = []
  for (const ref of targets) {
    try {
      await cancelModuleExecution({
        moduleId: ref.moduleId,
        executionId: ref.executionId,
        ...(reason === undefined ? {} : { reason }),
      })
      accepted.push(ref)
    } catch (error) {
      failures.push({ ref, error: asApiError(error) })
    }
  }
  return { batchId, accepted, failures }
}

export const useCancelBatchExecutions = createCommand<
  CancelBatchExecutionsVars,
  CancelBatchExecutionsResult
>({
  send: cancelBatchExecutions,
  invalidates: () => [keys.modules.all()],
})
